/**
 * Раздел каталога «Моя мебель» — шкафы и кухни из конструктора мебели.
 *
 * Элементы раздела не записаны в коде, а собираются из хранилища пользователя
 * (`useCustomFurnitureStore`) при каждом обращении. Для движков это обычный раздел:
 * `getCatalogItem` находит шкаф по ссылке, 3D берёт модель кодом по `view3d`, план —
 * символ раздела. Элемент для одного и того же проекта шкафа — один и тот же объект
 * (кэш по проекту), поэтому поиск раздела по элементу работает, как у констант.
 */
import { FurnitureModuleType } from "@/api";
import { LayerKind } from "@/shared/plan/layerKind";
import { CatalogAttach, type CatalogItem, type CatalogSection } from "../../types";
import { KITCHEN_RUN_LABELS } from "./constants";
import {
	KITCHEN_PIECE_SEPARATOR,
	kitchenPieces,
	kitchenSize,
	splitKitchenPieceId,
} from "./kitchen";
import { wardrobeFinishColor, wardrobeSize } from "./logic";
import { useCustomFurnitureStore } from "./store";
import type { KitchenDesign, ModuleDesign, WardrobeDesign } from "./types";

/** Префикс id 3D-модели шкафа из конструктора: дальше — id проекта шкафа */
export const CUSTOM_WARDROBE_MODEL = "custom-wardrobe:";
/** Префикс id 3D-модели кухни из конструктора: дальше — id проекта кухни */
export const CUSTOM_KITCHEN_MODEL = "custom-kitchen:";
/** Префикс id 3D-модели модуля из конструктора: дальше — id проекта модуля */
export const CUSTOM_MODULE_MODEL = "custom-module:";

const items = new WeakMap<WardrobeDesign | KitchenDesign | ModuleDesign, CatalogItem>();

/** Элемент каталога из проекта шкафа; размеры — из конструктора, менять их на плане нельзя */
const itemOf = (design: WardrobeDesign): CatalogItem => {
	const cached = items.get(design);
	if (cached) return cached;
	const color = wardrobeFinishColor(design);
	const { width, depth, height } = wardrobeSize(design);
	const item: CatalogItem = {
		id: design.id,
		subGroup: "Шкафы",
		name: design.name,
		description: `Шкаф из конструктора ${width} × ${depth}, высота ${height} мм.`,
		width,
		height,
		depth,
		elevation: 0,
		color,
		attach: CatalogAttach.Everywhere,
		view3d: {
			type: "component",
			id: `${CUSTOM_WARDROBE_MODEL}${design.id}`,
			width,
			height,
			depth,
			color,
		},
	};
	items.set(design, item);
	issued.add(item);
	return item;
};

/** Элемент каталога из проекта кухни: габарит — по всем сторонам и острову */
const kitchenItemOf = (kitchen: KitchenDesign): CatalogItem => {
	const cached = items.get(kitchen);
	if (cached) return cached;
	const { width, depth, height } = kitchenSize(kitchen);
	const item: CatalogItem = {
		id: kitchen.id,
		subGroup: "Кухни",
		name: kitchen.name,
		description: `Кухня из конструктора ${width} × ${depth} мм.`,
		width,
		height,
		depth,
		elevation: 0,
		color: kitchen.frontColor,
		attach: CatalogAttach.Everywhere,
		view3d: {
			type: "component",
			id: `${CUSTOM_KITCHEN_MODEL}${kitchen.id}`,
			width,
			height,
			depth,
			color: kitchen.frontColor,
		},
	};
	items.set(kitchen, item);
	issued.add(item);
	return item;
};

/** Элемент каталога из модуля: навесной висит на своей высоте */
const moduleItemOf = (module: ModuleDesign): CatalogItem => {
	const cached = items.get(module);
	if (cached) return cached;
	const item: CatalogItem = {
		id: module.id,
		subGroup: "Модули",
		name: module.name,
		description: `Модуль из конструктора ${module.width} × ${module.depth}, высота ${module.height} мм.`,
		width: module.width,
		height: module.height,
		depth: module.depth,
		elevation: module.type === FurnitureModuleType.Wall ? module.elevation : 0,
		color: module.frontColor,
		attach: CatalogAttach.Everywhere,
		view3d: {
			type: "component",
			id: `${CUSTOM_MODULE_MODEL}${module.id}`,
			width: module.width,
			height: module.height,
			depth: module.depth,
			color: module.frontColor,
		},
	};
	items.set(module, item);
	issued.add(item);
	return item;
};

/** Части кухни по сторонам — элементы для «Разделить на части»; кэш по проекту кухни */
const pieceItems = new WeakMap<KitchenDesign, Map<string, CatalogItem>>();
/** Всё, что выдал этот раздел: по этому множеству каталог узнаёт свои элементы */
const issued = new WeakSet<CatalogItem>();

const kitchenPieceItemOf = (kitchen: KitchenDesign, run: string): CatalogItem | null => {
	const cache = pieceItems.get(kitchen) ?? new Map<string, CatalogItem>();
	pieceItems.set(kitchen, cache);
	const cached = cache.get(run);
	if (cached) return cached;
	const piece = kitchenPieces(kitchen).find((p) => p.run === run);
	if (!piece) return null;
	const id = `${kitchen.id}${KITCHEN_PIECE_SEPARATOR}${run}`;
	const item: CatalogItem = {
		id,
		subGroup: "Кухни",
		name: `${kitchen.name} · ${KITCHEN_RUN_LABELS[piece.run].toLowerCase()}`,
		description: `Часть кухни «${kitchen.name}».`,
		width: piece.width,
		height: kitchen.height,
		depth: piece.depth,
		elevation: 0,
		color: kitchen.frontColor,
		attach: CatalogAttach.Everywhere,
		view3d: {
			type: "component",
			id: `${CUSTOM_KITCHEN_MODEL}${id}`,
			width: piece.width,
			height: kitchen.height,
			depth: piece.depth,
			color: kitchen.frontColor,
		},
	};
	cache.set(run, item);
	issued.add(item);
	return item;
};

/**
 * Элемент «Моей мебели» по id: модуль, шкаф, кухня целиком или часть кухни
 * («id#сторона»). Части в карточках каталога не показываются — они появляются
 * только у поставленной кухни после «Разделить на части».
 */
export const customFurnitureItem = (id: string): CatalogItem | null => {
	const { designs, kitchens, modules } = useCustomFurnitureStore.getState();
	const { kitchenId, run } = splitKitchenPieceId(id);
	if (run) {
		const kitchen = kitchens.find((k) => k.id === kitchenId);
		return kitchen ? kitchenPieceItemOf(kitchen, run) : null;
	}
	const module = modules.find((m) => m.id === id);
	if (module) return moduleItemOf(module);
	const wardrobe = designs.find((d) => d.id === id);
	if (wardrobe) return itemOf(wardrobe);
	const kitchen = kitchens.find((k) => k.id === id);
	return kitchen ? kitchenItemOf(kitchen) : null;
};

/** Выдан ли элемент разделом «Моя мебель» — включая части кухни, которых нет в списке */
export const isCustomFurnitureItem = (item: CatalogItem | object) =>
	issued.has(item as CatalogItem);

/** Кухня ли это из конструктора — по id элемента «Моей мебели» */
export const isCustomKitchen = (id: string) =>
	useCustomFurnitureStore.getState().kitchens.some((kitchen) => kitchen.id === id);

export const customFurnitureSection: CatalogSection<CatalogItem> = {
	kind: LayerKind.Furniture,
	label: "Моя мебель",
	canChangeDimensions: false,
	subGroups: ["Модули", "Шкафы", "Кухни"],
	get items() {
		const { designs, kitchens, modules } = useCustomFurnitureStore.getState();
		return [...modules.map(moduleItemOf), ...designs.map(itemOf), ...kitchens.map(kitchenItemOf)];
	},
};
